/** Protected route wrapper — redirects to /login when unauthenticated.
 *
 * Also restores the current user after a page refresh. The auth store persists
 * only the access/refresh tokens to localStorage; the `user` object lives in
 * memory and is lost on reload. Without it, `user.id` is undefined and any
 * per-user query (e.g. the running time-entry banner) silently drops its owner
 * filter and falls back to tenant-wide results. Re-fetching `/me` here, once,
 * when authenticated-but-user-missing, keeps the identity available for the
 * whole authenticated tree. */

import { useEffect } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@joint/jerry/stores";
import { refreshAccessToken } from "@joint/jerry/api";
import { fetchMeApi, type MeResponse } from "@joint/jerry/api/auth";

export default function ProtectedRoute() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const logout = useAuthStore((s) => s.logout);
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated || !accessToken || user) return;
    let cancelled = false;

    const applyMe = (data: MeResponse["data"]) => {
      if (cancelled) return;
      setUser({
        id: data.id,
        email: data.email,
        name: data.name,
        tenantId: data.tenant_id,
      });
    };

    const restore = async () => {
      try {
        applyMe((await fetchMeApi(accessToken)).data);
      } catch {
        // A failed /me is not necessarily a dead session: the stored access token
        // may simply be past its 30-min expiry while the refresh token is still
        // valid. Try a single refresh + retry before giving up — the page-load
        // half of the same forced-relogin bug the central client fixes via
        // fetchWithAuthRetry.
        try {
          const fresh = await refreshAccessToken();
          applyMe((await fetchMeApi(fresh)).data);
        } catch {
          // No refresh token stored, or the refresh/retry also failed → the
          // session really is gone; clear it so the user returns to /login
          // rather than staying half-authenticated with no identity.
          if (!cancelled) logout();
        }
      }
    };
    void restore();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, accessToken, user, setUser, logout]);

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <Outlet />;
}
